<template>
  <b-modal
    v-model="visible"
    title="Stack datasets"
    size="lg"
    hide-footer
    @hidden="close"
  >
    <p class="text-muted small mb-3">
      Stacking puts every point from each dataset into one new dataset and adds a
      column saying which dataset each point came from. Use it for the same kind of
      data collected at different times, such as a virus count each year. The new
      dataset is coloured by that column, using each dataset's own colour.
    </p>

    <b-form-group label="Label column" label-size="sm" description="The column added to every point. Each dataset's label goes in it.">
      <b-form-input v-model.trim="columnName" size="sm" :disabled="saving || !!savedDataset" />
    </b-form-group>

    <div class="table-responsive mb-3">
      <table class="table table-sm mb-0 stack-table">
        <thead>
          <tr>
            <th>Dataset</th>
            <th style="width: 30%">Label</th>
            <th style="width: 70px">Colour</th>
            <th class="text-right" style="width: 70px">Points</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.id">
            <td class="align-middle">{{ row.name }}</td>
            <td><b-form-input v-model.trim="row.label" size="sm" :disabled="saving || !!savedDataset" /></td>
            <td class="align-middle"><input v-model="row.color" type="color" class="stack-color" :disabled="saving || !!savedDataset" :aria-label="'Colour for ' + row.name" /></td>
            <td class="text-right align-middle">
              <span v-if="row.error" class="text-danger" :title="row.error">error</span>
              <span v-else-if="row.features === null"><i class="fas fa-sync fa-spin text-muted"></i></span>
              <span v-else>{{ row.features.length }}</span>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="3" class="text-right"><strong>Total</strong></td>
            <td class="text-right"><strong>{{ totalPoints }}</strong></td>
          </tr>
        </tfoot>
      </table>
    </div>

    <b-form-group label="New dataset name" label-size="sm">
      <b-form-input v-model.trim="datasetName" size="sm" :disabled="saving || !!savedDataset" />
    </b-form-group>

    <p v-if="problem" class="text-danger small mb-2"><i class="fas fa-exclamation-triangle"></i> {{ problem }}</p>

    <div v-if="savedDataset" class="alert alert-success small mb-0">
      <p class="mb-2">
        <i class="fas fa-check-circle"></i> Saved <strong>{{ savedDataset.name }}</strong>:
        {{ totalPoints }} points, coloured by <strong>{{ columnName }}</strong>.
      </p>
      <b-btn size="sm" variant="success" @click="openOnFarm">Open it on the farm map</b-btn>
      <b-btn size="sm" variant="link" @click="visible = false">Close</b-btn>
    </div>
    <div v-else class="d-flex justify-content-end" style="gap: 8px;">
      <b-btn size="sm" variant="outline-secondary" :disabled="saving" @click="visible = false">Cancel</b-btn>
      <b-btn size="sm" variant="success" :disabled="!canSave" @click="save">
        <i v-if="saving" class="fas fa-sync fa-spin"></i>
        <span v-else>Stack {{ rows.length }} datasets</span>
      </b-btn>
    </div>
  </b-modal>
</template>

<script>
import firebase from 'firebase/compat/app'
import 'firebase/compat/storage'
import { deriveHeaders } from '@/lib/deriveHeaders'
const fb = require('@/firebaseConfig.js')

// Same categorical palette the visualization settings use, for datasets
// that have no solid colour of their own.
const PALETTE = ['#ffff00', '#ff0000', '#00ffff', '#00ff00', '#1f738c', '#000018', '#e06666', '#f6b26b', '#ffd966', '#94c47d', '#70a8dc', '#8e7cc3', '#cc4125', '#c27ba0', '#77CCCC', '#777711', '#AA7744', '#AAAA44', '#774411', '#4477AA', '#AA4455', '#771155', '#DDDD77', '#117744', '#DDAA77', '#CC99BB', '#44AAAA', '#117777', '#77AADD', '#88CCAA', '#DD7788', '#114477', '#AA4488', '#771122', '#44AA77']
const YEAR = /\b(19|20)\d{2}\b/
const NUMERIC = /^-?\d+(\.\d+)?$/

export default {
  data () {
    return {
      visible: true,
      columnName: 'Source',
      datasetName: '',
      rows: [],
      saving: false,
      savedDataset: null,
      saveError: ''
    }
  },
  computed: {
    totalPoints () {
      return this.rows.reduce((n, r) => n + (r.features ? r.features.length : 0), 0)
    },
    problem () {
      if (this.saveError) return this.saveError
      if (this.rows.some(r => r.features === null)) return ''
      const failed = this.rows.filter(r => r.error)
      if (failed.length) return `Could not read ${failed.map(r => `"${r.name}"`).join(', ')}. Remove it from the selection and try again.`
      if (!this.columnName) return 'Give the label column a name.'
      if (this.rows.some(r => !r.label)) return 'Give every dataset a label.'
      const labels = this.rows.map(r => r.label)
      if (new Set(labels).size !== labels.length) return 'Each dataset needs a different label, or their points will be coloured the same.'
      if (!this.totalPoints) return 'These datasets contain no points.'
      if (!this.datasetName) return 'Name the new dataset.'
      return ''
    },
    canSave () {
      return !this.saving && !this.savedDataset && !this.problem && this.rows.length > 1 && this.rows.every(r => r.features)
    }
  },
  created () {
    const chosen = this.$store.state.dataJoiner.chosenDatasets || []
    const years = chosen.map(d => (YEAR.exec(d.name || '') || [])[0])
    const yearMode = chosen.length > 1 && years.every(Boolean) && new Set(years).size === years.length
    const used = new Set()
    this.rows = chosen.map((d, i) => {
      const own = d.vizSettings && d.vizSettings.colorBy === 'solid' && d.vizSettings.circleColor
      const color = own && !used.has(own.toLowerCase()) ? own : PALETTE.find(c => !used.has(c.toLowerCase())) || PALETTE[i % PALETTE.length]
      used.add(color.toLowerCase())
      return { id: d.id, name: d.name, folderId: d.folderId, label: yearMode ? years[i] : d.name, color, features: null, error: '', file: d.file }
    })
    if (yearMode) {
      this.columnName = 'Year'
      this.rows.sort((a, b) => a.label.localeCompare(b.label))
      const base = (chosen[0].name || 'Stacked').replace(YEAR, '').replace(/\s{2,}/g, ' ').replace(/^[\s\-_&,]+|[\s\-_&,]+$/g, '')
      this.datasetName = `${base || 'Stacked'} ${this.rows[0].label}-${this.rows[this.rows.length - 1].label}`
    } else {
      this.datasetName = `Stacked: ${chosen.map(d => d.name).join(' + ')}`.slice(0, 120)
    }
    this.rows.forEach(row => this.load(row))
  },
  methods: {
    // Every dataset kind, including collector datasets under
    // datasets/dynamic/{id}/, exposes its file at file.geoJsonPath.
    async load (row) {
      try {
        const path = row.file && (row.file.geoJsonPath || row.file.path)
        if (!path) throw new Error('no GeoJSON file')
        const url = await firebase.storage().ref(path).getDownloadURL()
        const gj = await (await fetch(url)).json()
        // Frozen: Vue 2 would otherwise make every coordinate reactive.
        row.features = Object.freeze((Array.isArray(gj.features) ? gj.features : []).filter(f => f && f.geometry))
      } catch (e) {
        row.error = (e && e.message) || 'read failed'
        row.features = []
      }
    },
    vizType (features) {
      const t = (features[0] && features[0].geometry && features[0].geometry.type) || 'Point'
      if (/Polygon/.test(t)) return 'polygon'
      if (/LineString/.test(t)) return 'line'
      return 'circle'
    },
    async save () {
      this.saving = true
      this.saveError = ''
      try {
        const col = this.columnName
        const numeric = this.rows.every(r => NUMERIC.test(r.label))
        const value = label => (numeric ? Number(label) : label)
        const features = []
        this.rows.forEach(r => r.features.forEach(f => {
          features.push({ type: 'Feature', geometry: f.geometry, properties: { ...(f.properties || {}), [col]: value(r.label) } })
        }))

        const farmId = this.$store.state.farm && this.$store.state.farm.id
        const uid = this.$store.state.currentUser && this.$store.state.currentUser.uid
        const path = `datasets/${farmId}/stacked-${Date.now()}.geojson`
        await firebase.storage().ref(path).putString(JSON.stringify({ type: 'FeatureCollection', features }), 'raw', {
          contentType: 'application/json',
          customMetadata: { userId: uid || '' }
        })

        const values = this.rows.map(r => value(r.label))
        const categories = this.rows.map(r => ({ name: String(value(r.label)), count: r.features.length, color: r.color }))
        const rowColors = this.rows.map(r => r.color)
        const vizSettings = {
          type: this.vizType(features),
          colorBy: 'valueBased',
          colorHeader: col,
          categorical: true,
          zoneClassification: 'categorical',
          featureCategories: categories,
          colors: rowColors.concat(PALETTE.filter(c => !rowColors.includes(c))),
          number: numeric,
          minValue: numeric ? Math.min(...values) : false,
          maxValue: numeric ? Math.max(...values) : false,
          zoneStops: 5,
          breaks: [],
          filters: [],
          opacity: 1,
          baseLayer: 'satellite',
          circleColor: '#ffc107',
          circleRadius: 2,
          circleRadiusMin: 5,
          circleRadiusMax: 20,
          borderColor: '#ffffff',
          borderWidth: 1,
          lineWidth: 2,
          minColor: '#ffc107',
          maxColor: '#056839',
          sizeBy: 'fixed',
          sizeHeader: false,
          sizeNumber: true,
          sizeCategorical: false,
          minSizeValue: false,
          maxSizeValue: false,
          labelBy: 'noLabel',
          labelHeader: false,
          labelColor: '#ffffff',
          labelSize: 14,
          heatBy: 'standard',
          heatRadius: 30,
          recalculateRange: false,
          recalculateJenks: false,
          showBlocks: false,
          subLayer: false
        }

        const doc = {
          name: this.datasetName,
          description: `Stacked from ${this.rows.length} datasets with the Data Joiner: ${this.rows.map(r => `${r.name} (${col} = ${r.label}, ${r.features.length} points)`).join('; ')}.`,
          headers: deriveHeaders({ features }),
          farmId,
          createdOn: new Date(),
          createdBy: uid,
          updatedOn: new Date(),
          updatedBy: uid,
          file: { geoJsonPath: path, path, originalFileName: 'stacked.geojson' },
          folderId: (this.rows[0] && this.rows[0].folderId) || 'root',
          dynamic: false,
          vizSettings
        }
        const ref = await fb.datasetsCollection.add(doc)
        this.savedDataset = { id: ref.id, ...doc }
      } catch (e) {
        this.saveError = 'Saving failed: ' + ((e && e.message) || e)
      } finally {
        this.saving = false
      }
    },
    async openOnFarm () {
      const farmId = this.$store.state.farm && this.$store.state.farm.id
      const dataset = this.savedDataset
      this.visible = false
      await this.$router.push(`/f/${farmId}`).catch(() => {})
      // Launch once the farm map has finished its own load.
      const started = Date.now()
      const tryLaunch = () => {
        const st = this.$store.state
        if (st.mapObject && !st.loadingMap) return this.$store.dispatch('launchDataset', { dataset })
        if (Date.now() - started < 15000) setTimeout(tryLaunch, 400)
      }
      setTimeout(tryLaunch, 800)
    },
    close () {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.stack-table td, .stack-table th { font-size: 0.9rem; }
.stack-color { width: 40px; height: 28px; padding: 0; border: 1px solid #ced4da; border-radius: 4px; background: none; cursor: pointer; }
</style>
